// Multi-Line Performance Chart Component
// Sprint 7: Shows NAV, Principal, and Gains on same chart with toggles

import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Box, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { formatCurrency } from '../../utils/navCalculations';
import { MultiLineChartData } from '../../utils/chartHelpers';

interface MultiLinePerformanceChartProps {
  data: MultiLineChartData[];
  height?: number;
}

const MultiLinePerformanceChart: React.FC<MultiLinePerformanceChartProps> = ({
  data,
  height = 300,
}) => {
  // State for toggling lines visibility
  const [showNAV, setShowNAV] = useState(true);
  const [showPrincipal, setShowPrincipal] = useState(true);
  const [showGains, setShowGains] = useState(true);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <Box
          sx={{
            backgroundColor: '#0A0A0A',
            border: '1px solid #FFD700',
            borderRadius: '8px',
            p: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold', mb: 1 }}>
            {label}
          </Typography>
          
          {showNAV && (
            <Typography variant="body2" sx={{ color: '#FFD700', mb: 0.5 }}>
              <strong>NAV:</strong> {formatCurrency(data.nav)}
            </Typography>
          )}
          
          {showPrincipal && (
            <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 0.5 }}>
              <strong>Principal:</strong> {formatCurrency(data.principal)}
            </Typography>
          )}
          
          {showGains && (
            <Typography
              variant="body2"
              sx={{ color: data.gains >= 0 ? '#4CAF50' : '#F44336', mb: 0.5 }}
            >
              <strong>Gains:</strong> {formatCurrency(data.gains)}
            </Typography>
          )}
          
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
            Return: {data.returnPct.toFixed(2)}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Line Toggle Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showNAV}
              onChange={(e) => setShowNAV(e.target.checked)}
              sx={{
                color: '#FFD700',
                '&.Mui-checked': { color: '#FFD700' },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#FFD700' }}>
              Total NAV
            </Typography>
          }
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={showPrincipal}
              onChange={(e) => setShowPrincipal(e.target.checked)}
              sx={{
                color: '#FFFFFF',
                '&.Mui-checked': { color: '#FFFFFF' },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
              Principal
            </Typography>
          }
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={showGains}
              onChange={(e) => setShowGains(e.target.checked)}
              sx={{
                color: '#4CAF50',
                '&.Mui-checked': { color: '#4CAF50' },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#4CAF50' }}>
              Total Gains
            </Typography>
          }
        />
      </Box>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          
          <XAxis
            dataKey="date"
            stroke="#E0E0E0"
            style={{ fontSize: '12px' }}
            reversed={false}
          />
          
          <YAxis
            stroke="#E0E0E0"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Legend
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="line"
          />

          {/* NAV Line */}
          {showNAV && (
            <Line
              type="monotone"
              dataKey="nav"
              stroke="#FFD700"
              strokeWidth={3}
              dot={{ fill: '#FFD700', r: 4 }}
              name="Total NAV"
              connectNulls
            />
          )}

          {/* Principal Line */}
          {showPrincipal && (
            <Line
              type="monotone"
              dataKey="principal"
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#FFFFFF', r: 3 }}
              name="Principal"
              connectNulls
            />
          )}

          {/* Gains Line */}
          {showGains && (
            <Line
              type="monotone"
              dataKey="gains"
              stroke="#4CAF50"
              strokeWidth={2}
              dot={{ fill: '#4CAF50', r: 3 }}
              name="Total Gains"
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend Note */}
      <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1, textAlign: 'center' }}>
        💡 Toggle lines on/off using the checkboxes above • Hover over chart for details
      </Typography>
    </Box>
  );
};

export default MultiLinePerformanceChart;
